import React, {
  memo, useCallback, useContext, useRef, useState,
} from 'react';
import {
  ActivityIndicator, FlatList, RefreshControl,
} from 'react-native';
import { Appbar, IconButton } from 'react-native-paper';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, VStack, HStack, Text,
} from 'native-base';
import { useToast } from 'react-native-toast-notifications';
import { MyNotifications, MyUnreadNotificationsCount } from '../../gql/notification/query';
import { MarkNotificationAsRead, MarkAllNotificationsAsRead, DeleteNotification } from '../../gql/notification/mutation';
import { AuthContext } from '../../context/AuthContext';
import LoadingIndicator from '../../components/commons/LoadingIndicator';

const PAGE_SIZE = 20;

const notificationRef = (cache, id) => cache.identify({ __typename: 'Notification', id });

// Memoized so that marking one notification as read doesn't redraw the whole list.
const NotificationCard = memo(({ notification, onMarkAsRead, onDelete }) => (
  <Box
    bg={notification.hasBeenRead ? 'gray.100' : 'white'}
    p={4}
    borderRadius="md"
    shadow={1}
  >
    <HStack justifyContent="space-between" alignItems="center">
      <VStack flex={1} mr={2}>
        <Text fontWeight="bold" fontSize="md" color="black">
          {notification.title}
        </Text>
        <Text fontSize="sm" color="gray.700" mt={1}>
          {notification.message}
        </Text>
        <Text fontSize="xs" color="gray.500" mt={1}>
          {new Date(notification.createdAt).toLocaleString()}
        </Text>
      </VStack>
      <HStack>
        {!notification.hasBeenRead && (
          <IconButton
            icon="check-circle-outline"
            color="green.500"
            size={24}
            onPress={() => onMarkAsRead(notification.id)}
          />
        )}
        {notification.hasBeenRead && (
          <IconButton
            icon="delete-outline"
            color="red.500"
            size={24}
            onPress={() => onDelete(notification.id)}
          />
        )}
      </HStack>
    </HStack>
  </Box>
));

function Notification() {
  const toast = useToast();
  const { authState } = useContext(AuthContext);
  const {
    data, loading, error, refetch, fetchMore,
  } = useQuery(MyNotifications, {
    variables: { limit: PAGE_SIZE },
    skip: !authState.authenticated,
  });
  // Shared with the header badge, which already keeps it up to date.
  const { data: unreadData } = useQuery(MyUnreadNotificationsCount, {
    skip: !authState.authenticated,
  });
  const unreadCount = unreadData?.myUnreadNotificationsCount ?? 0;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // A short page means there is nothing older. Refs: they must not trigger a redraw.
  const reachedEnd = useRef(false);
  const loadingMoreRef = useRef(false);

  const notifications = data?.myNotifications || [];

  // The cache is updated directly, so the screen reacts instantly with no refetch of the list.
  const [markAsRead] = useMutation(MarkNotificationAsRead, {
    update: (cache) => {
      cache.modify({
        fields: { myUnreadNotificationsCount: (count = 0) => Math.max(0, count - 1) },
      });
    },
  });
  const [markAllAsRead] = useMutation(MarkAllNotificationsAsRead, {
    optimisticResponse: { markAllNotificationsAsRead: { __typename: 'Status', status: 'success' } },
    update: (cache) => {
      notifications.forEach(({ id }) => {
        cache.modify({ id: notificationRef(cache, id), fields: { hasBeenRead: () => true } });
      });
      cache.modify({ fields: { myUnreadNotificationsCount: () => 0 } });
    },
  });
  const [deleteNotification] = useMutation(DeleteNotification);

  const handleMarkAsRead = useCallback(async (id) => {
    try {
      await markAsRead({ variables: { id } });
    } catch (e) {
      console.error(e);
      toast.show('Erreur lors de la mise à jour', { type: 'danger' });
    }
  }, [markAsRead, toast]);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.show('Toutes les notifications marquées comme lues', { type: 'success' });
    } catch (e) {
      console.error(e);
      toast.show('Erreur lors de la mise à jour', { type: 'danger' });
      refetch();
    }
  };

  const handleDeleteNotification = useCallback(async (id) => {
    try {
      await deleteNotification({
        variables: { id },
        update: (cache) => {
          cache.evict({ id: notificationRef(cache, id) });
          cache.gc();
        },
      });
      toast.show('Notification supprimée', { type: 'success' });
    } catch (e) {
      console.error(e);
      toast.show('Erreur lors de la suppression', { type: 'danger' });
    }
  }, [deleteNotification, toast]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || reachedEnd.current || notifications.length === 0) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const { data: more } = await fetchMore({
        variables: { limit: PAGE_SIZE, cursor: notifications[notifications.length - 1].id },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previous;
          const known = new Set(previous.myNotifications.map((n) => n.id));
          return {
            ...previous,
            myNotifications: [
              ...previous.myNotifications,
              ...fetchMoreResult.myNotifications.filter((n) => !known.has(n.id)),
            ],
          };
        },
      });
      if (more.myNotifications.length < PAGE_SIZE) reachedEnd.current = true;
    } catch (e) {
      console.error(e);
      toast.show('Erreur de chargement des notifications', { type: 'danger' });
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [fetchMore, notifications, toast]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const { data: first } = await refetch({ limit: PAGE_SIZE, cursor: undefined });
      reachedEnd.current = first.myNotifications.length < PAGE_SIZE;
    } catch (e) {
      console.error(e);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (error) {
    toast.show('Erreur de chargement des notifications', { type: 'danger' });
  }

  return (
    <>
      <Appbar.Header statusBarHeight={0}>
        <Appbar.Content title="Mes Notifications" />
        {unreadCount > 0 && (
          <IconButton icon="check-all" onPress={handleMarkAllAsRead} />
        )}
      </Appbar.Header>
      {loading && notifications.length === 0 ? (
        <LoadingIndicator text="Chargement des notifications..." />
      ) : (
        <FlatList
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: 16, gap: 12, flexGrow: 1 }}
          data={notifications}
          keyExtractor={(item) => String(item.id)}
          renderItem={({ item }) => (
            <NotificationCard
              notification={item}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDeleteNotification}
            />
          )}
          onEndReached={loadMore}
          onEndReachedThreshold={0.5}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
          ListEmptyComponent={(
            <Box p={4} alignItems="center">
              <Text color="gray.500">Aucune notification</Text>
            </Box>
          )}
          ListFooterComponent={loadingMore ? <ActivityIndicator style={{ margin: 16 }} /> : null}
        />
      )}
    </>
  );
}

export default Notification;
